import { useFlagWithDefaults } from 'flagging/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
// BEFORE CREATING A FLAG, READ FOR BEST PRACTICES:
// https://www.flagship.io/feature-flags-naming-conventions/
// https://docs.launchdarkly.com/guides/best-practices/creating-flags
// Flag naming convention:
// feature_{release/op/experiment/permission}_team_month_year{_temp, if temp}
// Do not use dots in the name, while allowed in LD,
// you cannot access flag with dots in the name in react/js
// We turned off auto camel casing on the provider, more here
// https://docs.launchdarkly.com/sdk/client-side/react/react-web#flag-keys
// Type of flags:
// RELEASE: a flag used to roll out a release (enable or disable), will be temporary
// OP: operations flag used to turn on and off operations tooling, may or may not be temporary
// EXPERIMENT: A/B and other testing flags, will likely be temporary
// PERMISSION: flag to control feature permissions, eg Only enterprise users have feature XYZ, will not be temporary
// Usage eg:
// import { FLAGS } from 'shared/utils/launchdarkly/flags';
// FLAGS.INTERNATIONALIZATION.useFlag();
// For temporary flags, like release flags, it is considered best practice to post a branch with
// the flag removed so that flag cleanup is as easy as possible.
export var FLAGS = {
    // TEMPLATE: {
    //  name        : 'feature_{release/op/experiment/permission}_team_month_year{_temp, if temp}',
    //  description : 'Feature flag to allow enterprise customers access to enterprise features',
    //  default     : 'false',
    //  values      : ['true', 'false'],
    //  useFlag     : () => useFlags().<TEMPLATE.name>,
    // },
    AUTOSAVE_DEBOUNCE_VALUE: {
        name: 'autosave_debounce_value',
        description: 'Number flag determining length of debounce for notes autosave',
        default: 0,
        values: [5000, 0],
        useFlag: function () { return useFlagWithDefaults('AUTOSAVE_DEBOUNCE_VALUE', 1000); },
    },
    SHOW_SCHEDULING_ON_SIDEBAR: {
        name: 'scheduling_permission_cgx_06_22_temp',
        description: 'Permissions flag allowing user to see and access Scheduling',
        default: false,
        values: ['true', 'false'],
        useFlag: function () { return useFlags().scheduling_permission_cgx_06_22_temp; },
    },
    SHOW_SCHEDULING_SETTINGS_ON_SIDEBAR: {
        name: 'scheduling_settings_permission_cgx_11_23_temp',
        description: 'Permissions flag allowing user to see and access Settings/schedule',
        default: false,
        values: ['true', 'false'],
        useFlag: function () { return useFlags().scheduling_settings_permission_cgx_11_23_temp; },
    },
    SHOW_SETTINGS_ON_SIDEBAR: {
        name: 'settings_permission_cgx_06_22_temp',
        description: 'Permissions flag allowing user to see and access Settings',
        default: false,
        values: ['true', 'false'],
        useFlag: function () { return useFlags().settings_permission_cgx_06_22_temp; },
    },
    SHOW_TOGGLE_BUTTON_FOR_CALENDAR_SYNC: {
        name: 'bidirectional_calendar_sync_6_27',
        description: 'Permissions flag to allow user to view and edit the toggle button!',
        default: false,
        values: ['true', 'false'],
        useFlag: function () { return useFlags().bidirectional_calendar_sync_6_27; },
    },
    SHOW_USERS_TAB_IN_SETTINGS: {
        name: 'settings_user_cgx_07_18_temp',
        description: 'Permissions flag allowing user to see and access the Users tab in Settings',
        default: false,
        values: ['true', 'false'],
        useFlag: function () { return useFlags().settings_user_cgx_07_18_temp; },
    },
    TEST_ROLES: {
        name: 'test_roles_07_19_temp',
        description: 'Roles to be used in rbac testing in the frontend. Supersedes what is given by the jwt token.',
        default: [],
        values: [[], ['group:admin', 'group:compass:coach']],
        useFlag: function () { return useFlags().test_roles_07_19_temp; },
    },
    SHOW_ACT_AS_ON_SIDEBAR: {
        name: 'view_as_permission_cgx_08_08_temp',
        description: 'Feature flag to allow Act As button to show on side bar',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().view_as_permission_cgx_08_08_temp; },
    },
    VIEW_AS_CREATE_FUNCTIONALITY: {
        name: 'view_as_create_functionality',
        description: 'Feature flag to allow creation of appointments in view as mode',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().view_as_create_functionality; },
    },
    VIEW_AS_EDIT_FUNCTIONALITY: {
        name: 'view_as_edit_functionality',
        description: 'Feature flag to allow editing of appointments in view as mode',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().view_as_edit_functionality; },
    },
    ENABLE_1116_DEF_OPEN: {
        name: 'enable_1116_def_open_08_22',
        description: 'Enable CGX-1116 CollapseCard default open',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_1116_DEF_OPEN', false); },
    },
    SHOW_USER_PROFILE_TAB_IN_SETTINGS: {
        name: 'show_user_profile_tab_in_settings_cgx_09_2022_temp',
        description: 'this flag enables/disables the profile tab in the settings page for a non admin user',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('SHOW_USER_PROFILE_TAB_IN_SETTINGS', false); },
    },
    KAREO_NOTES: {
        name: 'kareo_note_CGX_1614',
        description: 'enables viewing kareo notes',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('KAREO_NOTES', true); },
    },
    ENABLE_AUTOSAVE: {
        name: 'autosave_CGX_1581',
        description: 'Enables autosaving',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_AUTOSAVE', true); },
    },
    ENABLE_MM_COACHING_APPTS: {
        name: 'mm_coaching_appts_CGX_1520',
        description: 'Enables viewing MM and Coaching appointments on the interactions page',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_MM_COACHING_APPTS', true); },
    },
    ENABLE_SAVE_QUEUING: {
        name: 'cgx_1821_save_queuing',
        description: 'Enables queuing of repeated save attempts',
        default: false,
        values: ['true', 'false'],
        useFlag: function () { return useFlagWithDefaults('ENABLE_SAVE_QUEUING', false); },
    },
    PROFILE_APPOINTMENT_LOCATION: {
        name: 'profile_appointment_location_cgx_1887',
        description: "When enabled, uses the location link from the user's profile instead of the appointment",
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('PROFILE_APPOINTMENT_LOCATION', true); },
    },
    ENABLE_UPDATE_USERS: {
        name: 'show_update_user_cgx_10_2022_temp',
        description: 'enables the editing of users in their view',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_UPDATE_USERS', false); },
    },
    ENABLE_SAFETY_PLAN: {
        name: 'enable_safety_plan_cgx_3250',
        description: 'enables safety plan view in notes page',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_SAFETY_PLAN', false); },
    },
    SHOW_DATE_YEAR: {
        name: 'show_year_cgx_1962',
        description: 'Adds year to date formatting',
        default: 'different-year',
        values: ['none', 'always', 'different-year'],
        useFlag: function () { return useFlagWithDefaults('SHOW_DATE_YEAR', 'different-year'); },
    },
    NOTE_SETUP: {
        name: 'note_setup_3696',
        description: 'Controls the use of the single note setup endpoint instead of multiple on-demand calls from FE',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('NOTE_SETUP', false); },
    },
    SHOW_ADMINISTRATIVE_TAB_IN_SETTINGS: {
        name: 'administrative_tab_cgx_1673_11102022_temp',
        description: 'Permissions flag allowing user to see and access the Administrative tab in Scheduling',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().administrative_tab_cgx_1673_11102022_temp; },
    },
    ENABLE_DEFAULT_NOTE_TEMPLATE: {
        name: 'default_note_template_cgx_2488',
        description: 'Enables the selection of a default note template',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_UPDATE_USERS', false); },
    },
    ENABLE_MEASURES_MBC_UPDATES: {
        name: 'enable_measures_mbc_updates',
        description: 'Permissions flag for enabling new measures content related to MBC Push Notifications project',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().enable_measures_mbc_updates; },
    },
    UNDO_NOTE: {
        name: 'undo_note_CGX_2282',
        description: 'Enables undo note',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().undo_note_CGX_2282; },
    },
    PAGE_SELECT: {
        name: 'page_select_cgx_1812',
        description: 'Enables the updated page selector',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlags().page_select_cgx_1812; },
    },
    DOC_UPLOAD: {
        name: 'doc_upload_cgx_2271',
        description: 'Enables access to the document upload feature',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('DOC_UPLOAD', true); },
    },
    ALLOW_ROLE_EDITION: {
        name: 'allow_role_edition_CGX_2427',
        description: 'Enables admins to edit care provider and admin roles',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ALLOW_ROLE_EDITION', false); },
    },
    APPOINTMENT_ISSUE: {
        name: 'appointment_issue_cgx_3098',
        description: 'Enables appointment issue marking & hiding',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('APPOINTMENT_ISSUE', true); },
    },
    FETCH_TODO_NOTES_FROM_ONLY_EHR: {
        name: 'fetch_todo_notes_from_ehr_cgx_2774',
        description: 'Gets todo appointments and notes from EHR API instead of from both EHR API and Rotom',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('FETCH_TODO_NOTES_FROM_ONLY_EHR', false); },
    },
    FETCH_UPCOMING_NOTES_FROM_ONLY_EHR: {
        name: 'fetch_upcoming_notes_from_ehr_cgx_2776',
        description: 'Gets upcoming appointments and notes from EHR API instead of from both EHR API and Rotom',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('FETCH_UPCOMING_NOTES_FROM_ONLY_EHR', false); },
    },
    FETCH_PAST_INTERACTIONS_FROM_ONLY_EHR: {
        name: 'fetch_appointments_and_notes_from_ehr_cgx_3220',
        description: 'Gets appointments and notes from EHR API instead of from both EHR API and Rotom',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('FETCH_PAST_INTERACTIONS_FROM_ONLY_EHR', true); },
    },
    SPRING_HEALTH_CUSTOMER_IDS: {
        name: 'spring_health_customer_ids_cgx_3254',
        description: 'List of customer IDs that are Spring Health internal. Used to toggle some behavior for company employees.',
        default: [''],
        values: [''],
        useFlag: function () { return useFlagWithDefaults('SPRING_HEALTH_CUSTOMER_IDS', ['spring-internal-id']); },
    },
    ENABLE_MEDICATIONS_TAB: {
        name: 'enable_medications_tab',
        description: 'enables the medications tab',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_MEDICATIONS_TAB', false); },
    },
    ENABLE_SPRING_INTERNAL_MEMBER_WARNING: {
        name: 'spring_internal_member_warning',
        description: 'Used to determine whether or not to show spring internal member view warning',
        default: [false],
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_SPRING_INTERNAL_MEMBER_WARNING', false); },
    },
    ALLOWED_DOCUMENT_FILE_TYPES: {
        name: 'allowed_document_file_types_cgx_3445',
        description: 'File types that are allowed to be uploaded as documents',
        default: [''],
        values: [''],
        useFlag: function () { return useFlagWithDefaults('ALLOWED_DOCUMENT_FILE_TYPES', []); },
    },
    ENABLE_MEMBER_SEARCH: {
        name: 'enable_member_search_cgx_3741',
        description: 'enables the member search on side bar',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_MEMBER_SEARCH', false); },
    },
    MED_MANAGER_CAN_EDIT_NOTES: {
        name: 'med_manager_can_edit_notes_cgxp_49',
        description: 'Allows medical managers to view and edit notes',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('MED_MANAGER_CAN_EDIT_NOTES', false); },
    },
    ENABLE_SCHEDULE_CONSTRAINTS: {
        name: 'enable_schedule_constraints_cgx_03_23_temp',
        description: 'enables scheduling constraints on profile page',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_SCHEDULE_CONSTRAINTS', false); },
    },
    MEMBER_DEACTIVATING_COPY: {
        name: 'member_deactivating_copy_cgx_1190',
        description: 'Manages the copy that is displayed in the member deactivation banner',
        default: '',
        values: [''],
        useFlag: function () { return useFlagWithDefaults('MEMBER_DEACTIVATING_COPY', ''); },
    },
    ENABLE_NOTES_METHOD_DEFAULT_EMPTY: {
        name: 'default_notes_method_empty_cgxrev-217',
        description: 'enables setting notes empty as default',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_NOTES_METHOD_DEFAULT_EMPTY', false); },
    },
    AUTHENTICATE_ZENDESK_JWT: {
        name: 'authenticated-zendesk-jwt',
        description: 'enables authentication for zendesk via a diglet supplied jwt token',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('AUTHENTICATE_ZENDESK_JWT', false); },
    },
    VIEW_AS_IN_HOUSE_AVAILABILITY_FILTER: {
        name: 'view_as_filter_by_in_house_availability_cgxco_951_temp',
        description: 'Just show providers with in_house_availability_source_of_truth_at on view as user search input',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('VIEW_AS_IN_HOUSE_AVAILABILITY_FILTER', false); },
    },
    ENABLE_SIGNED_PAGING: {
        name: 'signed-paging-cgxp-170',
        description: 'Turns on a server side paged and sorted table in the frontend and starts using some dark paths on the backend.',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_SIGNED_PAGING', false); },
    },
    ENABLE_AUDIT_LOGS: {
        name: 'frontend_audit_logs',
        description: 'controls whether audit logs populated by useAuditLog are populated and sent to ehr-api',
        default: [false],
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_AUDIT_LOGS', false); },
    },
    MERGE_SCHEDULING_INTO_CG_PORTAL: {
        name: 'merge_scheduling_with_cg_portal_cgx_7_18_temp',
        description: 'controls whether we use the MFE version of scheduling or the version merged into cg-portal',
        default: [false],
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('MERGE_SCHEDULING_INTO_CG_PORTAL', false); },
    },
    ENABLE_MBC_PUSH_NOTIFICATIONS: {
        name: 'mbc_push_notifications_cgxp_1290',
        description: 'Enables MBC push notifications',
        default: false,
        values: [true, false],
        useFlag: function () { return useFlagWithDefaults('ENABLE_MBC_PUSH_NOTIFICATIONS', false); },
    },
};
